import { Lottie, LottieHandle } from 'lottie-react';
import React, { useRef, useState } from 'react';

import Button from '@/core/components/Button';
import FullScreenLoader from '@/core/components/FullScreenLoader';
import { ToastStatus } from '@/core/components/Toast/Toast';
import useMutation from '@/core/hooks/useMutation';
import { useEnvContext } from '@/core/lib/env/env.context';
import { ApiMethods, ApiVersions } from '@/core/lib/fetch';
import Query from '@/core/lib/new-architecture/query';
import Store from '@/core/lib/new-architecture/store';
import { badgeClassName } from '@/core/lib/new-architecture/store/badges.store';
import { useRouterContext } from '@/core/lib/router/router.context';
import Routes from '@/core/lib/router/routes';
import { useToastContext } from '@/core/lib/toast/toast.context';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import { CatType } from '@/core/pages/profile/PublicProfile/Status/BadgesTab';

const isCategoryType = (badgeCategory: string): badgeCategory is CatType => ['weight', 'co2', 'event'].includes(badgeCategory);

interface BadgesControlProps {}

const BadgesControl: React.FC<BadgesControlProps> = () => {
  const { data: current } = Store.current.useCurrent();
  const { data: badges, isLoading: isBadgesLoading } = Store.badges.useBadges();
  const [actualIndex, setActualIndex] = useState<number>(0);
  const lottieRef = useRef<LottieHandle>(null);
  const { push } = useRouterContext();
  const { t } = useTranslationContext(['common']);
  const { DONNONS_API_URL } = useEnvContext();
  const userInvalidate = Query.user.useInvalidate();
  const toast = useToastContext();

  const mutation = useMutation({
    apiDefinition: { method: ApiMethods.PUT, url: `${DONNONS_API_URL}${ApiVersions.V1}/user/control/badges` },
    onError: () => {
      toast?.open(t('errors.unknown', { ns: 'common' }), 2000, ToastStatus.DANGER);
    },
  });

  if (isBadgesLoading) {
    return <FullScreenLoader ariaLabel={t('loading', { ns: 'common' })} />;
  }

  const badgesFiltered = badges?.filter(({ alias }) => current?.control?.data?.badges?.includes(alias));

  if (!badgesFiltered?.length || !current) return null;

  const { title, description, isChecked, cat, unlockUrl } = badgesFiltered[actualIndex];

  const handleOnClickNextBadge = () => {
    if (actualIndex < badgesFiltered.length - 1) {
      setActualIndex(previousIndex => previousIndex + 1);
    }
  };

  const handleOnClickUpdateUserBadgesControl = async () => {
    await mutation.mutateAsync();
    await userInvalidate.invalidate();
    push(new Routes.PublicProfileRoute(current.uuid));
  };

  const hasAnimation = isChecked && !!unlockUrl;
  const checkedClassName = isCategoryType(cat) ? badgeClassName[cat] : '';

  return (
    <section className="flex flex-col items-center">
      <p className="text-body-primary mt-6 font-semibold">{t('control.badges.congratulations', { ns: 'common' })}</p>
      <p className="font-normal">{t('control.badges.new-badge', { ns: 'common', count: badgesFiltered.length })}</p>
      <div className="my-6 text-center">
        {hasAnimation && (
          <div className={`mx-auto size-60 overflow-hidden rounded-full ${checkedClassName}`}>
            <Lottie autoplay className="w-fit" lottieRef={lottieRef} src={unlockUrl} />
          </div>
        )}
        <p className="text-32 font-semibold text-mandarine-700">{title}</p>
        {description && <p className="text-body-secondary text-content-placeholder">{description}</p>}
      </div>

      {actualIndex < badgesFiltered.length - 1 ? (
        <Button type="button" onClick={handleOnClickNextBadge}>
          {t('control.badges.next', { ns: 'common' })}
        </Button>
      ) : (
        <>
          <Button type="button" onClick={handleOnClickUpdateUserBadgesControl}>
            {t('control.badges.see-all', { ns: 'common' })}
          </Button>
        </>
      )}
    </section>
  );
};

export default BadgesControl;
